import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { MinoLogo } from './chamaTheme';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";

const WalletIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="6" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M2 10h20" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M6 6V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <rect x="15" y="14" width="4" height="3" rx="1" fill="currentColor" opacity="0.7"/>
  </svg>
);
const TradeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="16 7 22 7 22 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const BallotIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/>
    <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M7 7l1.5 1.5L11 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const TrophyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M5 4H3a1 1 0 0 0-1 1v2a4 4 0 0 0 4 4h.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M19 4h2a1 1 0 0 1 1 1v2a4 4 0 0 1-4 4h-.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    <path d="M7 4h10v7a5 5 0 0 1-10 0V4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
);
const UserPlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
  </svg>
);
const AnalyticsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const CrownIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 17h18M5 17L3 7l4.5 4L12 4l4.5 7L21 7l-2 10H5Z"
      stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const API = '/api/chamas';

export default function ChamaDashboard({ chamaId, onNavigate }) {
  const { T } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [joinRequests, setJoinRequests] = useState([]);
  const [resolvingId, setResolvingId] = useState(null); // request ID currently being processed
  const token = localStorage.getItem('token');

  useEffect(() => { fetchChama(); }, [chamaId]);

  const fetchChama = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/${chamaId}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setData(d);
      // Fetch join requests if admin
      if (d?.my_role === 'admin') fetchJoinRequests();
    } finally { setLoading(false); }
  };

  const fetchJoinRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API}/${chamaId}/join-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setJoinRequests(d.requests || []);
    } catch { /* silent */ }
  }, [chamaId, token]);

  const resolveRequest = async (requestId, action) => {
    setResolvingId(requestId);
    try {
      const res = await fetch(`${API}/${chamaId}/join-requests/${requestId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        // Remove from local list immediately
        setJoinRequests(prev => prev.filter(r => r.id !== requestId));
        // If approved, refresh member list
        if (action === 'approve') fetchChama();
      } else {
        const d = await res.json();
        alert(d.error || 'Failed to process request');
      }
    } catch { alert('Network error'); }
    finally { setResolvingId(null); }
  };

  const generateInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await fetch(`${API}/${chamaId}/invite`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) setInviteData(d);
    } finally { setInviteLoading(false); }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteData.invite_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <PageLoader T={T} />;

  const { chama, members, my_role } = data;
  const isAdmin = my_role === 'admin';
  const canInvite = ['admin', 'treasurer', 'trader'].includes(my_role);
  const canSetLimit = ['admin', 'treasurer'].includes(my_role);
  const poolProgress = Math.min((parseFloat(chama.wallet_balance) / 500) * 100, 100);
  const isActive = chama.status === 'active';
  const pendingCount = joinRequests.length;

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text }}>
      <style>{`
        @media (max-width: 640px) {
  .chama-bottom-grid { grid-template-columns: 1fr !important; }
}
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:0.5} }
        .jr-row:hover { background: rgba(255,255,255,0.03) !important; }
        .resolve-btn { transition: all 0.15s; }
        .resolve-btn:hover { opacity: 0.85 !important; transform: scale(0.97); }
      `}</style>

      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 32px',
        background: `${T.bg}ee`, backdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MinoLogo size={26} />
          <div>
            <div style={{ fontSize: 10, color: T.textDim, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: FONT_MONO }}>Chama Mode</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
  <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{chama.name}</div>
  {chama.tier === 'premium' && (
    <span style={{
      fontSize: 9, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
      background: 'rgba(243,169,74,0.15)', color: T.gold,
      border: '1px solid rgba(243,169,74,0.3)', letterSpacing: '0.06em',
    }}>PRO</span>
  )}
  {chama.is_frozen && (
    <span style={{
      fontSize: 9, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
      background: 'rgba(244,63,94,0.15)', color: '#f43f5e',
      border: '1px solid rgba(244,63,94,0.3)', letterSpacing: '0.06em',
    }}>FROZEN</span>
  )}
{isAdmin && pendingCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                minWidth: 18, height: 18, borderRadius: 9, padding: '0 5px',
                background: '#ef4444', color: '#fff',
                fontSize: 9, fontWeight: 700, fontFamily: FONT_MONO,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'pulse 2s infinite',
              }}>
                {pendingCount}
              </span>
              <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 500 }}>
                {pendingCount} join request{pendingCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}


            </div>  {/* closes name/badges flex row */}
          </div>    {/* closes inner wrapper */}
        </div>      {/* closes outer flex container */}

        <a href="/dashboard" style={{
          fontSize: 13, color: T.textMuted, textDecoration: 'none',
          padding: '6px 14px', borderRadius: 8,
          border: `1px solid ${T.border}`, background: T.surface,
          fontFamily: FONT_SANS, transition: 'border-color 0.2s',
        }}>← Dashboard</a>
      </nav>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 80px', animation: 'fadeIn 0.4s ease' }}>

        {chama.is_frozen && (
          <div style={{
            background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.3)',
            borderRadius: 10, padding: '14px 20px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 20 }}>🔒</span>
            <div>
              <div style={{ color: '#f43f5e', fontWeight: 700, fontSize: 14 }}>Chama Frozen</div>
              <div style={{ color: T.textMuted, fontSize: 13, marginTop: 2 }}>
                {chama.frozen_reason || 'Under platform review'}. Deposits, withdrawals, and trades are blocked.
              </div>
            </div>
          </div>
        )}

        {/* Hero pool card */}
        <div style={{ ...card, padding: '28px 32px', marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textDim, marginBottom: 10 }}>
            Pool Balance
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 24 }}>
            <span style={{ color: T.textMuted, fontSize: 16, fontFamily: FONT_MONO }}>KES</span>
            <span style={{ color: T.text, fontSize: 48, fontWeight: 700, fontFamily: FONT_MONO, letterSpacing: '-0.03em', lineHeight: 1 }}>
              {parseFloat(chama.wallet_balance).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {!isActive && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: T.textMuted, fontSize: 12 }}>Pool activation progress</span>
                <span style={{ color: T.gold, fontSize: 12, fontFamily: FONT_MONO }}>{poolProgress.toFixed(0)}% of KES 500</span>
              </div>
              <div style={{ height: 3, background: T.border, borderRadius: 2 }}>
                <div style={{ height: '100%', borderRadius: 2, width: `${poolProgress}%`, background: T.gold, transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1)' }} />
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: T.border, borderRadius: 10, overflow: 'hidden' }}>
            {[
              { label: 'Total Contributed', value: `KES ${parseFloat(chama.total_contributed || 0).toLocaleString()}` },
{ label: 'Members', value: `${members.length} / ${chama.tier === 'premium' ? 25 : 10}` },
              { label: 'Mode', value: chama.vote_before_trade ? 'Vote-gated' : 'Open Trade' },
            ].map((s, i) => (
              <div key={i} style={{ background: T.surface, padding: '14px 16px' }}>
                <div style={{ color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{s.label}</div>
                <div style={{ color: T.text, fontSize: 14, fontWeight: 600, fontFamily: FONT_MONO }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>

       {/* Premium upgrade prompt — free tier, admin only */}
        {chama.tier !== 'premium' && isAdmin && (
          <UpgradePrompt chamaId={chamaId} token={token} T={T} onUpgraded={fetchChama} />
        )}

        {/* Action cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
         {[
            { label: 'Wallet',      icon: <WalletIcon />,    action: () => onNavigate('wallet'),      color: T.accent    },
            { label: 'Trade',       icon: <TradeIcon />,     action: () => navigate('/markets', { state: { chamaMode: true, chamaId, chamaName: chama.name, chamaBalance: parseFloat(chama.wallet_balance), voteBeforeTrade: chama.vote_before_trade } }), color: T.yes },
            { label: 'Votes',       icon: <BallotIcon />,    action: () => onNavigate('votes'),       color: T.gold      },
            { label: 'Chat',        icon: <ChatIcon />,      action: () => onNavigate('chat'),        color: '#06b6d4'   },
            { label: 'Disputes',    icon: <ShieldIcon />,    action: () => onNavigate('disputes'),    color: '#f43f5e'   },
            { label: 'Leaderboard', icon: <TrophyIcon />,    action: () => onNavigate('leaderboard'), color: '#a78bfa'   },
            ...(chama.tier === 'premium' ? [
              { label: 'Analytics', icon: <AnalyticsIcon />, action: () => onNavigate('analytics'),   color: T.gold      },
            ] : []),
          ].map((btn, i) => (
            <ActionCard key={i} {...btn} T={T} />
          ))}
        </div>

        {/* Members + Activity */}
        <div className="chama-bottom-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* Members card */}
          <div style={{ ...card, padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, letterSpacing: '-0.01em' }}>Members</h3>
              {canInvite && (
                <button onClick={generateInvite} disabled={inviteLoading} style={{
                  fontSize: 12, padding: '5px 12px', borderRadius: 7,
                  border: `1px solid ${T.border}`, background: T.surfaceHi,
                  color: T.textMuted, cursor: 'pointer', fontFamily: FONT_SANS,
                  opacity: inviteLoading ? 0.6 : 1,
                }}>
                  {inviteLoading ? '...' : 'Invite'}
                </button>
              )}
            </div>

            {inviteData && (
              <div style={{ background: T.accentDim, border: `1px solid ${T.accent}30`, borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ color: T.textMuted, fontSize: 11, marginBottom: 4 }}>Invite link (48h)</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{ color: T.accent, fontSize: 11, fontFamily: FONT_MONO, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inviteData.invite_url}
                  </code>
                  <button onClick={copyInvite} style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${copied ? T.yes : T.border}`,
                    background: copied ? T.yesDim : T.surfaceHi,
                    color: copied ? T.yes : T.textMuted,
                    fontFamily: FONT_SANS,
                  }}>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Join Requests (admin only) ── */}
            {isAdmin && joinRequests.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Join Requests
                  </span>
                  <span style={{
                    minWidth: 18, height: 18, borderRadius: 9, padding: '0 5px',
                    background: '#ef4444', color: '#fff',
                    fontSize: 10, fontWeight: 700, fontFamily: FONT_MONO,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {pendingCount}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {joinRequests.map(req => (
                    <JoinRequestRow
                      key={req.id}
                      request={req}
                      resolving={resolvingId === req.id}
                      onResolve={resolveRequest}
                      T={T}
                    />
                  ))}
                </div>

                {/* divider */}
                <div style={{ height: 1, background: T.border, marginBottom: 12 }} />
              </div>
            )}

            {/* Member list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
{members.map((m) => (
  <MemberRow
    key={m.user_id}
    member={m}
    T={T}
    isAdmin={isAdmin}
    chamaId={chamaId}
    token={token}
    onRoleChange={fetchChama}
    canSetLimit={canSetLimit}
  />
))}
            </div>
          </div>

          {/* Activity */}
          <ActivityPanel chamaId={chamaId} token={token} T={T} />
        </div>
      </div>
    </div>
  );
}

// ─── Join Request Row ─────────────────────────────────────────────────────────
function JoinRequestRow({ request, resolving, onResolve, T }) {
  const initials = (request.username || '??').slice(0, 2).toUpperCase();
  const timeAgo = (() => {
    const diff = Date.now() - new Date(request.created_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })();

  return (
    <div className="jr-row" style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px', borderRadius: 8,
      background: 'rgba(167,139,250,0.06)',
      border: '1px solid rgba(167,139,250,0.2)',
      transition: 'background 0.15s',
    }}>
      {/* Avatar */}
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: '#a78bfa', fontFamily: FONT_MONO,
      }}>{initials}</div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: T.text, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {request.username}
        </div>
        <div style={{ color: T.textDim, fontSize: 10, fontFamily: FONT_MONO }}>{timeAgo}</div>
      </div>

      {/* Approve / Reject */}
      {resolving ? (
        <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${T.border}`, borderTop: `2px solid #a78bfa`, animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
      ) : (
        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
          <button className="resolve-btn" onClick={() => onResolve(request.id, 'approve')} style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${T.yes}44`, background: `${T.yes}14`,
            color: T.yes, fontFamily: FONT_SANS, fontWeight: 600,
          }}>
            Approve
          </button>
          <button className="resolve-btn" onClick={() => onResolve(request.id, 'reject')} style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${T.no}33`, background: `${T.no}0d`,
            color: T.no, fontFamily: FONT_SANS, fontWeight: 500,
          }}>
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Existing sub-components (unchanged) ─────────────────────────────────────
function ActionCard({ label, icon, action, color, T }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={action} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? T.surfaceHi : T.surface,
        border: `1px solid ${hover ? T.borderHi : T.border}`,
        borderRadius: 12, padding: '18px 16px',
        cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.15s', color,
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        fontFamily: FONT_SANS,
      }}>
      <div style={{ marginBottom: 10 }}>{icon}</div>
      <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{label}</div>
    </button>
  );
}

function MemberRow({ member, T, isAdmin, chamaId, token, onRoleChange, canSetLimit }) {
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitVal, setLimitVal] = useState('');
  const [limitLoading, setLimitLoading] = useState(false);

  const initials = member.username?.slice(0, 2).toUpperCase() || '??';
  const roleColor = { admin: T.accent, trader: T.yes, treasurer: T.gold, member: T.textMuted }[member.role] || T.textMuted;

  const saveLimit = async () => {
    setLimitLoading(true);
    try {
      const res = await fetch(`/api/chamas/${chamaId}/members/${member.user_id}/spend-limit`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ spend_limit: limitVal === '' ? null : parseFloat(limitVal) }),
      });
      if (res.ok) { setEditingLimit(false); onRoleChange(); }
      else { const d = await res.json(); alert(d.error); }
    } catch { alert('Failed to update spend limit'); }
    finally { setLimitLoading(false); }
  };

  return (
    <div style={{ borderRadius: 8, background: T.surfaceHi, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px' }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          background: T.accentDim, border: `1px solid ${T.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, color: T.accent,
        }}>{initials}</div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ color: T.text, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.username}</div>
          <div style={{ color: T.textDim, fontSize: 11, fontFamily: FONT_MONO }}>{parseFloat(member.share_pct).toFixed(1)}% share</div>
        </div>
        {isAdmin && member.role !== 'admin' ? (
          <select
            value={member.role}
            onChange={async (e) => {
              const role = e.target.value;
              try {
                await fetch(`/api/chamas/${chamaId}/members/${member.user_id}/role`, {
                  method: 'PATCH',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ role }),
                });
                onRoleChange();
              } catch { alert('Failed to update role'); }
            }}
            style={{
              background: `${roleColor}14`, border: `1px solid ${roleColor}28`,
              borderRadius: 5, padding: '2px 7px', fontSize: 10, fontWeight: 600,
              color: roleColor, textTransform: 'uppercase', cursor: 'pointer',
              fontFamily: FONT_MONO, outline: 'none',
            }}>
            <option value="member">MEMBER</option>
            <option value="trader">TRADER</option>
            <option value="treasurer">TREASURER</option>
          </select>
        ) : (
          <div style={{
            color: roleColor, fontSize: 10, fontWeight: 600,
            background: `${roleColor}14`, border: `1px solid ${roleColor}28`,
            borderRadius: 5, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{member.role}</div>
        )}
      </div>

      {/* Spend limit row — admin and treasurer only, not for other admins */}
      {canSetLimit && member.role !== 'admin' && (
        <div style={{
          borderTop: `1px solid ${T.border}`, padding: '6px 10px',
          display: 'flex', alignItems: 'center', gap: 8, background: T.surface,
        }}>
          <span style={{ color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', flexShrink: 0 }}>
            Spend limit
          </span>
          {editingLimit ? (
            <>
              <input
                type="number"
                value={limitVal}
                onChange={e => setLimitVal(e.target.value)}
                placeholder="KES amount (blank = no limit)"
                autoFocus
                style={{
                  flex: 1, background: T.surfaceHi, border: `1px solid ${T.border}`,
                  borderRadius: 5, padding: '3px 8px', color: T.text,
                  fontFamily: FONT_MONO, fontSize: 11, outline: 'none',
                }}
              />
              <button onClick={saveLimit} disabled={limitLoading} style={{
                fontSize: 10, padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                border: `1px solid ${T.yes}44`, background: `${T.yes}14`,
                color: T.yes, fontFamily: FONT_SANS, fontWeight: 600,
              }}>{limitLoading ? '...' : 'Save'}</button>
              <button onClick={() => setEditingLimit(false)} style={{
                fontSize: 10, padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                border: `1px solid ${T.border}`, background: 'transparent',
                color: T.textMuted, fontFamily: FONT_SANS,
              }}>Cancel</button>
            </>
          ) : (
            <>
              <span style={{ flex: 1, color: member.spend_limit ? T.gold : T.textDim, fontSize: 11, fontFamily: FONT_MONO }}>
                {member.spend_limit ? `KES ${parseFloat(member.spend_limit).toLocaleString()}` : 'No limit'}
              </span>
              <button onClick={() => { setLimitVal(member.spend_limit ? String(member.spend_limit) : ''); setEditingLimit(true); }} style={{
                fontSize: 10, padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                border: `1px solid ${T.border}`, background: 'transparent',
                color: T.textMuted, fontFamily: FONT_SANS,
              }}>Edit</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityPanel({ chamaId, token, T }) {
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    fetch(`/api/chamas/${chamaId}/activity`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setActivity(d.activity || []));
  }, [chamaId]);

  const iconMap = {
    deposit:       { icon: '+', color: T.yes },
    withdrawal:    { icon: '-', color: T.gold },
    trade_placed:  { icon: '↑', color: T.accent },
    member_joined: { icon: '•', color: T.yes },
    member_left:   { icon: '•', color: T.no },
    vote_created:  { icon: '◎', color: '#a78bfa' },
    vote_resolved: { icon: '◎', color: T.gold },
    chama_created: { icon: '★', color: T.accent },
    join_requested:{ icon: '→', color: '#a78bfa' },
  };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px' }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 13, fontWeight: 600, color: T.text, letterSpacing: '-0.01em' }}>Activity</h3>
      {activity.length === 0 ? (
        <div style={{ color: T.textDim, fontSize: 13, textAlign: 'center', paddingTop: 20 }}>No activity yet</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {activity.slice(0, 12).map((a, i) => {
            const ic = iconMap[a.type] || { icon: '·', color: T.textDim };
            return (
              <div key={a.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < activity.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  background: `${ic.color}14`, border: `1px solid ${ic.color}28`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: ic.color, fontSize: 11, fontWeight: 700,
                }}>{ic.icon}</div>
                <div>
                  <div style={{ color: T.textMuted, fontSize: 12, lineHeight: 1.4 }}>{a.description}</div>
                  <div style={{ color: T.textDim, fontSize: 10, marginTop: 2, fontFamily: FONT_MONO }}>
                    {new Date(a.created_at).toLocaleString('en-KE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PageLoader({ T }) {
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${T.border}`, borderTop: `2px solid ${T.accent}`, animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function UpgradePrompt({ chamaId, token, T, onUpgraded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);

  useEffect(() => {
    fetch('/api/users/profile', { headers: { Authorization: `Bearer ${token}` } })
  .then(r => r.json())
  .then(d => setWalletBalance(parseFloat(d.user?.balance || d.balance || 0)));
  }, []);

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/chamas/${chamaId}/upgrade`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Upgrade failed'); return; }
      setModalOpen(false);
      onUpgraded();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { label: 'Max members',    free: '10',        pro: '25'               },
    { label: 'Analytics',      free: '—',         pro: 'Full dashboard'   },
    { label: 'Trade history',  free: 'Basic',     pro: 'Advanced export'  },
    { label: 'Support',        free: 'Standard',  pro: 'Priority'         },
  ];

  const insufficient = walletBalance !== null && walletBalance < 500;

  return (
    <>
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        .upgrade-cta { transition: opacity 0.15s, transform 0.15s; }
        .upgrade-cta:hover { opacity: 0.88; transform: translateY(-1px); }
        .upgrade-confirm { transition: opacity 0.15s, transform 0.15s; }
        .upgrade-confirm:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
      `}</style>

      {/* ── Compact strip ── */}
      <div style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 12, marginBottom: 20,
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CrownIcon size={16} color={T.gold} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.gold }}>Upgrade to Premium</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>
              25 members · Analytics dashboard · Priority support
            </div>
          </div>
        </div>
        <button className="upgrade-cta" onClick={() => setModalOpen(true)} style={{
          background: 'linear-gradient(135deg, #f3a94a, #9b5de5)',
          color: '#fff', border: 'none', borderRadius: 8,
          padding: '9px 18px', fontWeight: 700, cursor: 'pointer',
          fontSize: 13, fontFamily: FONT_SANS, flexShrink: 0, whiteSpace: 'nowrap',
        }}>
          Upgrade  KES 500
        </button>
      </div>

      {/* ── Modal ── */}
      {modalOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setModalOpen(false); setError(''); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 16, width: '100%', maxWidth: 440,
            overflow: 'hidden',
            animation: 'modalIn 0.22s cubic-bezier(0.22,1,0.36,1)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          }}>

            {/* Modal header */}
            <div style={{
              padding: '22px 24px 18px',
              borderBottom: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <CrownIcon size={18} color={T.gold} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: '-0.02em' }}>
                    Upgrade to Premium
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: T.gold,
                    background: `${T.gold}18`, border: `1px solid ${T.gold}30`,
                    borderRadius: 5, padding: '2px 8px', fontFamily: FONT_MONO,
                    letterSpacing: '0.05em',
                  }}>ONE-TIME</span>
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
                  Unlock the full potential of your chama.
                </div>
              </div>
              <button onClick={() => { setModalOpen(false); setError(''); }} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: T.textDim, fontSize: 20, lineHeight: 1, padding: '2px 4px',
                fontFamily: FONT_SANS,
              }}>×</button>
            </div>

            {/* Feature table */}
            <div style={{ padding: '0 24px' }}>
              {/* Column labels */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 72px 96px',
                padding: '12px 0 6px',
              }}>
                <div />
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Free</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.gold,    textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Premium</div>
              </div>

              {features.map((f, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '1fr 72px 96px',
                  padding: '10px 0',
                  borderTop: `1px solid ${T.border}`,
                }}>
                  <div style={{ fontSize: 12, color: T.textMuted, alignSelf: 'center' }}>{f.label}</div>
                  <div style={{ fontSize: 12, color: T.textDim, textAlign: 'center', fontFamily: FONT_MONO, alignSelf: 'center' }}>{f.free}</div>
                  <div style={{ fontSize: 12, color: T.gold, fontWeight: 600, textAlign: 'center', fontFamily: FONT_MONO, alignSelf: 'center' }}>{f.pro}</div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px 22px', borderTop: `1px solid ${T.border}`, marginTop: 4 }}>

              {/* Balance line */}
              {walletBalance !== null && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 12,
                  padding: '8px 12px', borderRadius: 8,
                  background: T.surfaceHi, border: `1px solid ${T.border}`,
                }}>
                  <span style={{ fontSize: 12, color: T.textMuted }}>Your wallet balance</span>
                  <span style={{
                    fontSize: 13, fontWeight: 700, fontFamily: FONT_MONO,
                    color: insufficient ? '#f43f5e' : T.yes,
                  }}>
                    KES {walletBalance.toLocaleString()}
                    {insufficient && <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 6, color: '#f43f5e' }}>— insufficient</span>}
                  </span>
                </div>
              )}

              {error && (
                <div style={{ color: '#f43f5e', fontSize: 12, marginBottom: 10 }}>{error}</div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="upgrade-confirm"
                  onClick={handleUpgrade}
                  disabled={loading || insufficient}
                  style={{
                    flex: 1,
                    background: loading || insufficient ? T.surfaceHi : 'linear-gradient(135deg, #f3a94a, #9b5de5)',
                    color: loading || insufficient ? T.textDim : '#fff',
                    border: 'none', borderRadius: 8,
                    padding: '11px 0', fontWeight: 700,
                    cursor: loading || insufficient ? 'not-allowed' : 'pointer',
                    fontSize: 14, fontFamily: FONT_SANS,
                  }}
                >
                  {loading ? 'Upgrading...' : 'Confirm — KES 500'}
                </button>
                <button
                  onClick={() => { setModalOpen(false); setError(''); }}
                  disabled={loading}
                  style={{
                    padding: '11px 20px', borderRadius: 8, cursor: 'pointer',
                    background: 'transparent', color: T.textMuted,
                    border: `1px solid ${T.border}`, fontSize: 13,
                    fontFamily: FONT_SANS,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}